import React, { useState, useEffect } from 'react';
import { 
  FaSchool, 
  FaStar, 
  FaMapMarkerAlt, 
  FaFilter, 
  FaTimes, 
  FaSort, 
  FaSortAmountDown,
  FaSortAmountUp 
} from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import SchoolProfile from './SchoolProfile';

/**
 * Component for displaying school search results with filtering options
 */
const SchoolResults = ({ searchTerm, useLocation, userLocation }) => {
  const { showError } = useToast();
  
  // Schools state
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Filtering and sorting state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    curriculum: [],
    location: '',
    rating: 0,
    fees: {
      min: '',
      max: '',
    },
    boardingOptions: [],
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Fetch schools on component mount
  useEffect(() => {
    fetchSchools();
  }, []);
  
  // Apply search, filters, and sorting when they change
  useEffect(() => {
    const filtered = filterSchools();
    sortSchools(filtered);
  }, [schools, searchTerm, filters, sortBy, sortOrder, useLocation, userLocation]);
  
  // Fetch schools from API
  const fetchSchools = async () => {
    try {
      setLoading(true);
      
      // Fetch from backend
      const response = await api.get('/schools/public');
      const data = response?.data?.data || response?.data?.schools || response?.data || [];
      const list = Array.isArray(data) ? data : [];
      setSchools(list);
      setFilteredSchools(list);
    } catch (error) {
      console.error('Error fetching schools:', error);
      showError('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter schools based on search term and filters
  const filterSchools = () => {
    let result = [...schools];
    
    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        school => 
          school.name.toLowerCase().includes(search) ||
          (school.location?.county && school.location.county.toLowerCase().includes(search)) ||
          (school.location?.town && school.location.town.toLowerCase().includes(search)) ||
          (Array.isArray(school.curriculum) && school.curriculum.some(curr => curr.toLowerCase().includes(search))) ||
          (typeof school.curriculum === 'string' && school.curriculum.toLowerCase().includes(search))
      );
    }
    
    // Apply curriculum filter
    if (filters.curriculum.length > 0) {
      result = result.filter(school => {
        if (!school.curriculum) return false;
        
        if (Array.isArray(school.curriculum)) {
          return filters.curriculum.some(curr => school.curriculum.includes(curr));
        } else {
          return filters.curriculum.includes(school.curriculum);
        }
      });
    }
    
    // Apply location filter
    if (filters.location) {
      const location = filters.location.toLowerCase();
      result = result.filter(
        school => 
          (school.location?.county && school.location.county.toLowerCase().includes(location)) ||
          (school.location?.town && school.location.town.toLowerCase().includes(location))
      );
    }
    
    // Apply rating filter
    if (filters.rating > 0) {
      result = result.filter(school => school.rating >= filters.rating);
    }
    
    // Apply fees filter
    if (filters.fees.min) {
      result = result.filter(school => 
        school.fees?.termly && school.fees.termly >= parseFloat(filters.fees.min)
      );
    }
    
    if (filters.fees.max) {
      result = result.filter(school => 
        school.fees?.termly && school.fees.termly <= parseFloat(filters.fees.max)
      );
    }
    
    // Apply boarding options filter
    if (filters.boardingOptions.length > 0) {
      result = result.filter(school => {
        if (filters.boardingOptions.includes('boarding') && school.hasBoarding) {
          return true;
        }
        if (filters.boardingOptions.includes('day') && school.hasDaySchool) {
          return true;
        }
        return false;
      });
    }
    
    // Apply location-based sorting if using location
    if (useLocation && userLocation) {
      // Calculate distance for each school that has coordinates
      result = result.map(school => {
        if (school.location?.coordinates) {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            school.location.coordinates.lat,
            school.location.coordinates.lng
          );
          return { ...school, distance };
        }
        return { ...school, distance: Infinity };
      });
    }
    
    return result;
  };
  
  // Sort filtered schools
  const sortSchools = (schools) => {
    let sorted = [...schools];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => {
          const comparison = a.name.localeCompare(b.name);
          return sortOrder === 'asc' ? comparison : -comparison;
        });
        break;
      case 'rating':
        sorted.sort((a, b) => {
          const comparison = (b.rating || 0) - (a.rating || 0);
          return sortOrder === 'asc' ? -comparison : comparison;
        });
        break;
      case 'fees':
        sorted.sort((a, b) => {
          const aFees = a.fees?.termly || 0;
          const bFees = b.fees?.termly || 0;
          const comparison = aFees - bFees;
          return sortOrder === 'asc' ? comparison : -comparison;
        });
        break;
      case 'distance':
        if (useLocation && userLocation) {
          sorted.sort((a, b) => {
            const comparison = (a.distance || Infinity) - (b.distance || Infinity);
            return sortOrder === 'asc' ? comparison : -comparison;
          });
        }
        break;
      // Relevance is default and already handled by the API or filterSchools
      default:
        if (useLocation && userLocation) {
          // If using location, sort by distance for relevance
          sorted.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
        }
        break;
    }
    
    setFilteredSchools(sorted);
  };
  
  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance;
  };
  
  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };
  
  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (filterType === 'curriculum' || filterType === 'boardingOptions') {
        // For array filters, toggle the value
        const currentValues = [...prevFilters[filterType]];
        const index = currentValues.indexOf(value);
        
        if (index === -1) {
          currentValues.push(value);
        } else {
          currentValues.splice(index, 1);
        }
        
        return { ...prevFilters, [filterType]: currentValues };
      } else if (filterType === 'fees') {
        // For fees object, update specific property
        return { 
          ...prevFilters, 
          fees: { ...prevFilters.fees, ...value }
        };
      } else {
        // For other filters, just set the value
        return { ...prevFilters, [filterType]: value };
      }
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      curriculum: [],
      location: '',
      rating: 0,
      fees: {
        min: '',
        max: '',
      },
      boardingOptions: [],
    });
    
    // Reset sort to default
    setSortBy('relevance');
    setSortOrder('desc');
  };
  
  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with default desc order
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  // View school profile
  const viewSchoolProfile = (school) => {
    setSelectedSchool(school);
    setShowProfileModal(true);
  };
  
  // Close school profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
  };
  
  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };
  
  // Format distance
  const formatDistance = (distance) => {
    if (!distance || distance === Infinity) return null;
    
    return distance < 1 
      ? `${Math.round(distance * 1000)} m`
      : `${distance.toFixed(1)} km`;
  };
  
  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-2xl font-bold flex items-center mb-4 md:mb-0">
            <FaSchool className="mr-2 text-primary" /> Schools
            {!loading && <span className="ml-2 text-base font-normal text-gray-600">({filteredSchools.length} results)</span>}
          </h2>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
            >
              <FaFilter className="mr-2" /> 
              {showFilters ? 'Hide Filters' : 'Filters'}
            </button>
            
            <div className="relative inline-block">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="form-select pr-10 pl-3 py-2 bg-gray-100 text-gray-700 rounded-md appearance-none"
              >
                <option value="relevance">Relevance</option>
                <option value="name">School Name</option>
                <option value="rating">Rating</option>
                <option value="fees">Fees</option>
                {useLocation && userLocation && <option value="distance">Distance</option>}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                {sortOrder === 'asc' ? <FaSortAmountUp className="text-gray-500" /> : <FaSortAmountDown className="text-gray-500" />}
              </div>
            </div>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              <FaSort />
            </button>
          </div>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Curriculum Filter */}
              <div>
                <h4 className="font-medium mb-2">Curriculum</h4>
                <div className="space-y-2">
                  {['8-4-4', 'CBC', 'IGCSE', 'IB'].map(curr => (
                    <label key={curr} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.curriculum.includes(curr)}
                        onChange={() => handleFilterChange('curriculum', curr)}
                        className="form-checkbox mr-2"
                      />
                      <span>{curr}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Location Filter */}
              <div>
                <h4 className="font-medium mb-2">Location</h4>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="form-input w-full"
                  placeholder="County or town..."
                />
              </div>
              
              {/* Rating Filter */}
              <div>
                <h4 className="font-medium mb-2">Minimum Rating</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.rating}
                    onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
                    className="form-range w-full"
                  />
                  <span>{filters.rating === 0 ? 'Any' : `${filters.rating}+`}</span>
                </div>
              </div>
              
              {/* Fees Filter */}
              <div>
                <h4 className="font-medium mb-2">Fees Range (KES)</h4>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={filters.fees.min}
                    onChange={(e) => handleFilterChange('fees', { min: e.target.value })}
                    className="form-input w-full"
                    placeholder="Min"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.fees.max}
                    onChange={(e) => handleFilterChange('fees', { max: e.target.value })}
                    className="form-input w-full"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              {/* Boarding Options */}
              <div>
                <h4 className="font-medium mb-2">School Type</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.boardingOptions.includes('boarding')}
                      onChange={() => handleFilterChange('boardingOptions', 'boarding')}
                      className="form-checkbox mr-2"
                    />
                    <span>Boarding School</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.boardingOptions.includes('day')}
                      onChange={() => handleFilterChange('boardingOptions', 'day')}
                      className="form-checkbox mr-2"
                    />
                    <span>Day School</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <FaTimes className="mr-1" /> Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Schools List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft">
          {filteredSchools.length === 0 ? (
            <div className="p-8 text-center">
              <FaSchool className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Schools Found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search criteria or filters</p>
              <button
                onClick={resetFilters}
                className="btn-primary"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredSchools.map((school) => (
                <div 
                  key={school._id} 
                  className="border border-gray-200 rounded-lg hover:border-primary transition-colors overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    {school.photos && school.photos.length > 0 ? (
                      <img 
                        src={school.photos[0]} 
                        alt={school.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <FaSchool className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    {useLocation && userLocation && school.distance && (
                      <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded shadow-md text-xs font-medium flex items-center">
                        <FaMapMarkerAlt className="text-primary mr-1" />
                        {formatDistance(school.distance)}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{school.name}</h3>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <FaMapMarkerAlt className="mr-1" />
                      <span>
                        {typeof school.location === 'object' && school.location !== null ? (
                          <>
                            {school.location.county || 'Location'}{school.location.town ? `, ${school.location.town}` : ''}
                          </>
                        ) : (
                          'Location'
                        )}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        {renderRatingStars(school.rating || 0)}
                        <span className="ml-1 text-sm text-gray-500">({school.reviewCount || 0})</span>
                      </div>
                      {school.fees?.termly && (
                        <div className="text-sm font-medium">
                          KES {school.fees.termly.toLocaleString()} / term
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {school.curriculum && Array.isArray(school.curriculum) ? (
                        school.curriculum.map((curr, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {typeof curr === 'object' ? JSON.stringify(curr) : curr}
                          </span>
                        ))
                      ) : school.curriculum ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {typeof school.curriculum === 'object' ? JSON.stringify(school.curriculum) : school.curriculum}
                        </span>
                      ) : null}
                      {school.hasBoarding && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Boarding
                        </span>
                      )}
                      {school.hasDaySchool && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          Day School
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => viewSchoolProfile(school)}
                      className="w-full btn-primary-outline text-sm py-2"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* School Profile Modal */}
      {selectedSchool && (
        <Modal
          isOpen={showProfileModal}
          onClose={closeProfileModal}
          title={selectedSchool.name}
          size="xl"
        >
          <SchoolProfile school={selectedSchool} />
        </Modal>
      )}
    </div>
  );
};

export default SchoolResults; 